import "@/assets/styles/main.css";
import { createInertiaApp } from "@inertiajs/svelte";
import { mount } from "svelte";
import type { ResolvedComponent } from "@inertiajs/svelte";
import routesData from "@/generated/routes.json";
import { initSentry, readInitialSentryConfig } from "@/lib/sentry";

// Initialize Sentry as early as possible from the backend-delivered `sentryConfig` page prop
// (ADR-6 — one DSN, no VITE_SENTRY_DSN). On initial load the prop is embedded in the #app element's
// `data-page`; browserTracingIntegration() then continues the server's trace from the <meta> tags.
// No-op when Sentry is disabled (config absent).
initSentry(readInitialSentryConfig(document.getElementById("app")));

// Make routes available globally for the route() helper
if (typeof window !== "undefined") {
  // Convert routes.json format to the format expected by route() helper
  const routes: Record<string, string> = {};
  for (const [name, def] of Object.entries(routesData.routes)) {
    routes[name] = def.uri;
  }
  window.routes = routes;
  window.__LITESTAR_ROUTES__ = routesData;
}

createInertiaApp({
  // Navigation progress bar. Inertia enables it by default (createInertiaApp's
  // `progress` defaults to `{}`), with a 250ms delay — so it only appears when a
  // visit actually waits (slow/DB-backed pages), never on fast navigations. We
  // override the color to a bright "skye" blue — a clearer, lighter accent than
  // the muted dark primary — instead of the stock blue (#29d).
  // `var(--color-skye-400)` resolves against :root (Tailwind v4 `@theme static`),
  // so it tracks the theme with no hardcoded hex.
  progress: { color: "var(--color-skye-400)" },
  // Lazy resolution: import.meta.glob without `eager` returns a map of
  // `() => Promise<module>` loaders. createInertiaApp's ComponentResolver
  // accepts `ResolvedComponent | Promise<ResolvedComponent>`, so we return
  // the loader's promise directly (the matched view is the only chunk fetched).
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("./views/**/*.svelte");
    const importPage = pages[`./views/${name}.svelte`];
    if (!importPage) {
      throw new Error(`Unknown Inertia view: ${name} (resources/views/${name}.svelte)`);
    }
    // LAYOUT RESOLUTION (Svelte adapter, @inertiajs/svelte 2.x):
    // Svelte has no `page.default.layout` auto-wrapping like Vue. The adapter's
    // `ResolvedComponent` type is `{ default; layout? }` where `layout` is a
    // Svelte component (or array of components, applied outer->inner), or a
    // LayoutResolver. A view opts into a layout by exporting `layout` from its
    // module context: `<script module>export { default as layout } from "..."</script>`.
    // The live layouts (EmptyLayout/AppShell/CustomerLayout) are attached
    // per-view via that `layout` export. To set a global
    // default layout instead, assign it here after the import resolves, e.g.:
    //   return importPage().then((page) => { page.layout = page.layout ?? Layout; return page; });
    return importPage();
  },
  setup({ el, App, props }) {
    if (el) {
      mount(App, { target: el, props });
    }
  },
});
